import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchData } from 'utils';
import useError from 'hooks/useError';
import useSmartlook from 'hooks/useSmartlook';
import { Constraints } from 'hooks/webapi/application/useConstraintsQuery';

export function useUpsaleDisplayQuery() {
  const { showError } = useError();
  const { error } = useSmartlook();
  return useQuery<ApplicationUpsale>({
    queryKey: ['upsale', 'display'],
    queryFn: async () => {
      try {
        const res = await fetchData('/client/application/upsale/display');

        return res;
      } catch (e) {
        error(
          '[useUpsaleDisplayQuery][GET /client/application/upsale/display]',
          e,
        );
        showError();
        throw e;
      }
    },
  });
}

export function useUpsaleRejectMutation() {
  const { showError } = useError();
  const { error } = useSmartlook();
  return useMutation({
    mutationFn: async () => {
      try {
        const data = await fetchData('/client/application/upsale/reject', {
          method: 'post',
        });

        return data;
      } catch (e) {
        error(
          '[useUpsaleRejectMutation][POST /client/application/upsale/reject]',
          e,
        );
        showError();
        throw e;
      }
    },
  });
}

export function useUpsaleConstraintsQuery() {
  const { showError } = useError();
  const { error } = useSmartlook();
  return useQuery<UpsaleConstraints>({
    queryKey: ['upsale', 'constraints'],
    queryFn: async () => {
      try {
        const res = await fetchData('/client/application/upsale/constraints');

        return res;
      } catch (e) {
        error(
          '[useUpsaleConstraintsQuery][GET /client/application/upsale/constraints]',
          e,
        );
        showError();
        throw e;
      }
    },
  });
}

export function useClientConstraintsQuery(enabled = true) {
  const { showError } = useError();
  const { error } = useSmartlook();
  return useQuery<Constraints>({
    queryKey: ['client', 'application', 'constraints'],
    queryFn: async () => {
      try {
        const res = await fetchData('/client/application/constraints');

        return res;
      } catch (e) {
        error(
          '[useClientConstraintsQuery][GET /client/application/constraints]',
          e,
        );
        showError();
        throw e;
      }
    },
    enabled,
  });
}

export type ApplicationUpsale = {
  amount: number;
};

type Constraint = {
  max: number;
  min: number;
  defaultValue: number;
  step: number;
};

export type UpsaleConstraints = {
  amountInterval: Constraint;
  termInterval: Constraint;
};
